import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useToast } from "../components/ui";
import { requireResponse } from "../core/errors/requireResponse";
import { SILENT, dashKey, useDashboardActions } from "./useDashboardData";

/**
 * The action queue's writes: done / undo / snooze / move-to-tomorrow, each a
 * POST /api/dashboard/actions/state { key, state, minutes? }.
 *
 *  - Done is optimistic: the row ticks at once and stays on screen, struck
 *    through, for the rest of the visit (the API drops done items from the
 *    list, so we keep our own copy). A failed write un-ticks it and says so.
 *  - Snooze / tomorrow remove the row at once; a failed write puts it back.
 *  - Every write re-fetches the queue and My Day (its counters read the same
 *    rows) once it lands, and marks the Store / Reports numbers stale (they
 *    refetch when that tab is next shown). The optimistic change is written to
 *    the query cache, so the user's stored copy (dashboardCache) follows it too.
 */

// requireResponse: the global interceptor RESOLVES a 5xx as undefined — that
// must count as a failure here, or an un-saved tick would look saved.
// `due_at` (the item's when.at) rides along on "done" so Reports can tell
// whether it was done on time.
export const postActionState = async (key, state, minutes, dueAt) => {
  const body = { key, state };
  if (minutes) body.minutes = minutes;
  if (dueAt) body.due_at = dueAt;
  return requireResponse(await axios.post("/api/dashboard/actions/state", body, SILENT));
};

const firstWords = (title = "") => String(title).split(" · ")[0];

// Run `fn` over `items` at most `limit` at a time — "move 30 to tomorrow" must
// not fire 30 requests at once.
export const runCapped = async (items, limit, fn) => {
  const results = [];
  for (let i = 0; i < items.length; i += limit) {
    results.push(...(await Promise.allSettled(items.slice(i, i + limit).map(fn))));
  }
  return results;
};

export function useDashboardActionQueue({ repId, storeKey, enabled }) {
  const qc = useQueryClient();
  const toast = useToast();
  const query = useDashboardActions(repId, storeKey, enabled);
  const actionsKey = dashKey("actions", storeKey, repId ?? "default");

  // key → item, for rows ticked during this visit.
  const [doneLocal, setDoneLocal] = useState(() => new Map());
  useEffect(() => setDoneLocal(new Map()), [storeKey, repId]);

  const refresh = useCallback(() => {
    const s = storeKey ?? "home";
    qc.invalidateQueries({ queryKey: ["dashboard", "actions", s] });
    qc.invalidateQueries({ queryKey: ["dashboard", "my-day", s] });
    // A payment link, a booked collection or a done action moves store / report
    // numbers too; not on screen now, so only marked stale (no fetch yet).
    qc.invalidateQueries({ queryKey: ["dashboard", "store", s], refetchType: "none" });
    qc.invalidateQueries({ queryKey: ["dashboard", "reports", s], refetchType: "none" });
  }, [qc, storeKey]);

  const serverItems = useMemo(() => (Array.isArray(query.data?.items) ? query.data.items : []), [query.data]);

  // Server rows (open) + this visit's ticked rows, ranked. A ticked row the
  // server still returns (refetch not landed yet) is shown once, as done.
  const items = useMemo(() => {
    const seen = new Set();
    const out = serverItems.map((it) => {
      seen.add(it.key);
      return doneLocal.has(it.key) ? { ...it, done: true } : it;
    });
    for (const [key, it] of doneLocal) if (!seen.has(key)) out.push({ ...it, done: true });
    return out.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
  }, [serverItems, doneLocal]);

  const open = useMemo(() => items.filter((it) => !it.done), [items]);

  const setLocal = (fn) => setDoneLocal((prev) => { const next = new Map(prev); fn(next); return next; });

  const markDone = useCallback(async (item, { silent = false } = {}) => {
    setLocal((m) => m.set(item.key, item));
    try {
      await postActionState(item.key, "done", null, item.when?.at || null);
      // Saved: drop it from the cached (and stored) list — this visit still shows
      // it struck through from doneLocal; a reload must not show it open again.
      dropFromCache([item.key]);
      if (!silent) toast.success(`Done · ${firstWords(item.title)}`);
    } catch {
      setLocal((m) => m.delete(item.key));
      toast.error("Couldn't mark that done — try again.");
    } finally {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, toast, actionsKey.join("|")]);

  const undoDone = useCallback(async (item) => {
    setLocal((m) => m.delete(item.key));
    try {
      await postActionState(item.key, "open");
    } catch {
      setLocal((m) => m.set(item.key, item));
      toast.error("Couldn't reopen that action — try again.");
    } finally {
      refresh();
    }
  }, [refresh, toast]);

  // Optimistically drop rows from the cached queue; returns a restore().
  const dropFromCache = (keys) => {
    const prev = qc.getQueryData(actionsKey);
    if (prev?.items) qc.setQueryData(actionsKey, { ...prev, items: prev.items.filter((it) => !keys.includes(it.key)) });
    return () => prev && qc.setQueryData(actionsKey, prev);
  };

  const snooze = useCallback(async (item, minutes = 15) => {
    const restore = dropFromCache([item.key]);
    try {
      await postActionState(item.key, "snoozed", minutes);
      toast.info(`Snoozed ${minutes} min · ${firstWords(item.title)}`, {
        action: { label: "Undo", onClick: () => postActionState(item.key, "open").finally(refresh) },
      });
    } catch {
      restore();
      toast.error("Couldn't snooze that action — try again.");
    } finally {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, toast, actionsKey.join("|")]);

  const moveToTomorrow = useCallback(async (list) => {
    if (!list.length) return;
    const restore = dropFromCache(list.map((it) => it.key));
    const results = await runCapped(list, 4, (it) => postActionState(it.key, "tomorrow"));
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed === list.length) {
      restore();
      toast.error("Couldn't move those actions — try again.");
    } else if (failed) {
      toast.warning(`Moved ${list.length - failed} to tomorrow · ${failed} couldn't be moved`);
    } else {
      toast.success(`${list.length} action${list.length === 1 ? "" : "s"} moved to tomorrow morning`);
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, toast, actionsKey.join("|")]);

  return { query, items, open, markDone, undoDone, snooze, moveToTomorrow, refresh };
}
